import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { CaseListItem, CaseListResponse } from "../api/types.ts"
import { useApiQuery, useDebounced } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { formatTracking } from "../lib/tracking.ts"
import { StatusBadge } from "./badges.tsx"
import { Icon, Input } from "../ui/index.ts"

/** 목록과 같은 300ms. 스캐너가 번호를 한 번에 쏟아부어도 한 번만 찾는다. */
const SEARCH_DEBOUNCE_MS = 300
const MAX_RESULTS = 8

/**
 * 어디서든 ⌘K 로 여는 통합 검색.
 *
 * 반품 건을 찾는 길이 지금까지 "목록 화면으로 가서 → 검색칸에 치고 → 줄을 누른다" 뿐이었다.
 * 전화로 주문번호를 불러 주는 동안 그 세 단계를 밟는 것이 이 업무에서 가장 잦은 동작이다.
 * 서버 검색은 목록과 같은 `q`(주문번호·반송장번호·고객명)를 쓴다 — 찾는 규칙이 두 벌이 되지 않게.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const [text, setText] = useState("")
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const settled = useDebounced(text.trim(), SEARCH_DEBOUNCE_MS)
  const query = useApiQuery<CaseListResponse>(
    open && settled !== "" ? `/api/cases?q=${encodeURIComponent(settled)}&size=${MAX_RESULTS}` : null,
  )
  const rows = query.data?.items ?? []

  // 열 때마다 처음부터. 지난번 검색어가 남아 있으면 엉뚱한 건으로 뛴다.
  useEffect(() => {
    if (!open) return
    setText("")
    setCursor(0)
    inputRef.current?.focus()
  }, [open])

  useEffect(() => setCursor(0), [settled])

  if (!open) return null

  function go(row: CaseListItem) {
    onClose()
    void navigate(`/cases/${row.id}`)
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      onClose()
      return
    }
    if (rows.length === 0) return
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setCursor((current) => (current + 1) % rows.length)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setCursor((current) => (current - 1 + rows.length) % rows.length)
    } else if (event.key === "Enter") {
      event.preventDefault()
      go(rows[cursor] ?? rows[0])
    }
  }

  return (
    <div className="palette-overlay" onMouseDown={onClose}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label={t("search.title")}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="palette__input">
          <Input
            ref={inputRef}
            icon="search"
            value={text}
            placeholder={t("search.placeholder")}
            aria-label={t("search.placeholder")}
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        <div className="palette__body">
          {settled === "" ? (
            <p className="palette__hint">{t("search.hint")}</p>
          ) : query.loading ? (
            <p className="palette__hint">{t("common.loading")}</p>
          ) : rows.length === 0 ? (
            <p className="palette__hint">{t("search.empty")}</p>
          ) : (
            <ul className="palette__list">
              {rows.map((row, index) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className={index === cursor ? "palette__item is-cursor" : "palette__item"}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => go(row)}
                  >
                    <span className="palette__main">
                      <strong>{row.orderNo}</strong>
                      <span className="muted">{row.customerName}</span>
                    </span>
                    <span className="palette__meta">
                      <span className="muted num">{formatTracking(row.trackingNo) ?? "—"}</span>
                      <StatusBadge status={row.status} />
                      <span className="muted">
                        {locale === "ja" ? row.orgNameJa : row.orgNameKo}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="palette__foot muted">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> {t("search.move")}
          </span>
          <span>
            <kbd>Enter</kbd> {t("search.open")}
          </span>
          <span>
            <kbd>Esc</kbd> {t("common.close")}
          </span>
          <span className="palette__grow" />
          <span>
            <Icon name="search" size={12} /> {t("search.scope")}
          </span>
        </div>
      </div>
    </div>
  )
}
